import i18n from 'i18next';
import findLastIndex from 'lodash/findLastIndex';
import unique from 'lodash/uniq';
import Routes from '../../constants/routes';
import { world } from '../../languages';
import { AVAILABLE_WORLDS, WORLD_LANGUAGE_CODES, languageVariations } from '../../languages/constants';
import { MaterialType } from '../../types/material-page';
import analytics from '../../services/analytics';
import qualityEvents from '../../services/quality-events/quality-events';
import enhancedEcommerce, { CatalogProps } from '../enhanced-ecommerce';
import formatPrice from '../price/format-price';
import twoDigitsFormat from '../two-digits-format';
import serializeQueryParams from '../url/serialize-query-params';
import extractMaterialDetails, { ExtractedMaterialDetails } from './extract-material-details';

export function formatDateWithMonthName(date: Date) {
    const locale = WORLD_LANGUAGE_CODES[world as keyof typeof WORLD_LANGUAGE_CODES];

    return date.toLocaleDateString(locale, {
        day: '2-digit',
        month: 'long',
        year: 'numeric'
    });
}

export const saleExpirationLabel = (materialInfo: Material, format?: 'digits' | 'monthName'): string => {
    let date = '';

    if (materialInfo.sale) {
        let { expireAt } = materialInfo.sale;
        const zoneIndex = expireAt.indexOf('+');

        if (zoneIndex > 0) {
            expireAt = expireAt.substring(0, zoneIndex);
        }

        if (expireAt) {
            const expirationDate = new Date(expireAt);
            const to = i18n.t('common:common.to');

            if (format === 'monthName') {
                date = `${to} ${formatDateWithMonthName(expirationDate)}.`;
            } else {
                date = `${i18n.t('common:common.to')} ${twoDigitsFormat(expirationDate.getDate())}.${twoDigitsFormat(
                    expirationDate.getMonth() + 1
                )}.${expirationDate.getFullYear()}`;
            }
        }
    }

    return date;
};

export const cutOutZeroCategory = (materialInfo: Material): void => {
    const categories = materialInfo.materialCategories;

    if (categories && categories[0] && categories[0].id === 0) {
        categories.shift();
    }

    materialInfo.materialCategories = categories;
};

export const formStringFromArr = (...args: [string, ExtractedMaterialDetails]) => extractMaterialDetails(...args);

export const formLanguages = (material: Material) => {
    let languages = [] as string[];

    if (material.bundle && material.languages) {
        languages = material.languages;
    } else if (material.language) {
        languages = [material.language];
    }

    if (!languageVariations[world]) {
        return;
    }

    const filteredLanguages = languageVariations[world]
        .filter((item) => languages.includes(item.value))
        .map((item) => item.title);

    return filteredLanguages.join(', ');
};

export const formBestsellers = (materialInfo: Material): FullPathWithLandingPagesEntity[][] => {
    const { bestInCategories } = materialInfo;
    const categories = materialInfo?.materialCategories?.slice();
    const paths: FullPathWithLandingPagesEntity[][] = [];
    const terminalCategories: FullPathWithLandingPagesEntity[][] = [];

    categories?.forEach((item) => paths.push(item.fullPathWithLandingPages));

    bestInCategories.forEach((bestsellerItem) => {
        let found = false;

        paths.forEach((path) => {
            if (!found) {
                const index = findLastIndex(path, {
                    materialCategoryId: bestsellerItem.id
                });

                if (index >= 0) {
                    terminalCategories.push(path.slice(0, index + 1));
                    found = true;
                }
            }
        });
    });

    return terminalCategories;
};

export const trackMaterialClickEvent = (
    material: Material,
    props: CatalogProps,
    source?: string,
    variant?: string,
    extra?: Record<string, string | number>
): void => {
    qualityEvents.materialPage.materialClick({ material, props, source, variant, extra });

    if (world !== AVAILABLE_WORLDS.DE && (props.internalPath === 'hp_bs' || props.internalPath === 'hp_reco_v3')) {
        analytics.push({
            event: 'gtmEvent',
            eventAction: 'HPClickFirstSlider',
            eventCategory: 'mkt-conversion',
            eventLabel: '',
            eventNonInteraction: true
        });
    }
};

export const centsToFloat = (price: Price): string => {
    price = String(price);

    if (price === '0') {
        return '0,00';
    }

    price = price.length < 3 ? `0${price}` : `${price}`;

    const splited = price.split('').reverse();

    splited.splice(2, 0, ',');
    price = splited.reverse().join('');

    return price;
};

export const hasMaterialAccess = (item: Material) => Boolean(item.hasAccess || item.aclId || item.hasAclFree);

export const isFreeMaterial = (item: Material) => {
    const price = formatPrice(item.price);

    return price === '0,00';
};

export const getBundleListTotal = ({ bundleMaterials, bundleListTotal }: Material): number =>
    // we added bundleListTotal to optimize search response, but some sliders still don't have bundleListTotal property, so we implemented such a workararound
    bundleMaterials ? bundleMaterials.length : bundleListTotal;

export const defaultMetaTitle = (materialInfo: Material): string => {
    let titles = [] as string[];
    let title: string;

    materialInfo?.materialCategories?.forEach((item) => {
        titles.push(item.fullPathWithLandingPages[0].title);
    });

    titles = unique(titles);

    if (titles.length > 1) {
        title = i18n.t('meta:meta.MATERIAL.title1', { subjects: titles.join(' & '), title: materialInfo.title });
    } else {
        title = i18n.t('meta:meta.MATERIAL.title2', { subject: titles[0] ? titles[0] : '', title: materialInfo.title });
    }

    return title;
};

export const defaultMetaDescription = (material: Material): string => {
    const titles = [] as string[];
    let description: string;

    material?.materialCategories?.forEach((item) => {
        titles.push(item.fullPathWithLandingPages[item.fullPathWithLandingPages.length - 1].title);
    });

    if (material.materialPrice === i18n.t('common:common.materialBlock.free')) {
        description = i18n.t('meta:meta.MATERIAL.description1', {
            classes: formStringFromArr('classGrades', material.materialClassGrades),
            subject: titles[0] ? titles[0] : '',
            totalPages: material.totalPages
        });
    } else {
        description = i18n.t('meta:meta.MATERIAL.description2', {
            classes: formStringFromArr('classGrades', material.materialClassGrades),
            subject: titles[0] ? titles[0] : '',
            totalPages: material.totalPages
        });
    }

    return description;
};

export const getMetaKeywords = (material: MaterialType): string => {
    let keywordsList: string[] = [];

    if (material.tags?.length) {
        keywordsList = [...material.tags];
    }

    if (material.materialCategories?.length) {
        material.materialCategories.forEach(
            (category) => !keywordsList.includes(category.title) && keywordsList.push(category.title)
        );
    }

    return keywordsList.reduce((val, key, idx) => `${val}${idx === 0 ? '' : ', '}${key}`, '');
};

export const setPosition = (index: number, limit = 36): number => {
    const params = serializeQueryParams() as UrlSearchParams;

    if (window.location.pathname.indexOf(Routes.SEARCH_RESULT.path) >= 0) {
        return params.p ? (params.p - 1) * limit + Number(index) : Number(index);
    }

    return Number(index);
};

export const handleMouseDown = (
    material: Material,
    props: CatalogProps,
    extra?: Record<string, string | number>
): void => {
    let source = 'catalog';

    if (window.location.pathname.indexOf(Routes.SEARCH_RESULT.path) >= 0) {
        source = 'search';
    }
    trackMaterialClickEvent(material, props, source, undefined, extra);
    enhancedEcommerce.tracker.productClick(material, props);
};

